"use client";

import { Fragment } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { AuthorAvatar, ShareButton, PostBreadcrumb } from "@/components/ui/";

interface PostArticleHeaderProps {
  backHref: string;
  backLabel: string;
  current: string;
  categoryLabel: string;
  accentColor: string;
  meta: string[];
  headline: string;
  initial?: string;
  avatarColor?: string;
  authorName?: string;
  authorSubtitle?: string;
  tags: string[];
}

export default function PostArticleHeader({
  backHref,
  backLabel,
  current,
  categoryLabel,
  accentColor,
  meta,
  headline,
  initial,
  avatarColor,
  authorName,
  authorSubtitle,
  tags,
}: PostArticleHeaderProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="relative pt-20 sm:pt-24">
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8"
      >
        <PostBreadcrumb
          backHref={backHref}
          backLabel={backLabel}
          current={current}
          className="mb-6 sm:mb-8"
        />

        <div className="mb-4 flex flex-wrap items-center gap-3 sm:mb-5">
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ color: accentColor, background: accentColor + "18" }}
          >
            {categoryLabel}
          </span>
          {meta.map((item, i) => (
            <Fragment key={item}>
              {i > 0 && <span className="text-muted-foreground/40">·</span>}
              <span className="text-muted-foreground text-xs">{item}</span>
            </Fragment>
          ))}
        </div>

        <h1 className="text-foreground mb-5 text-3xl leading-tight font-black tracking-tight sm:mb-6 sm:text-4xl lg:text-5xl">
          {headline}
        </h1>

        <div className="border-border flex flex-wrap items-center justify-between gap-4 border-b pb-6 sm:pb-8">
          {authorName ? (
            <div className="flex flex-wrap items-center gap-3">
              <AuthorAvatar initial={initial ?? ""} color={avatarColor ?? ""} />
              <div>
                <p className="text-foreground text-sm font-semibold">{authorName}</p>
                <p className="text-muted-foreground text-xs">{authorSubtitle}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-muted-foreground border-border rounded-full border px-2.5 py-1 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div />
          )}
          <ShareButton title={headline} />
        </div>
      </motion.div>
    </section>
  );
}
